import axios from "axios";
import Cookies from "js-cookie";

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  totalOrders?: number;
  activeUsers?: number;
  totalRevenue?: number;
  averageOrderValue?: number;
  currentMonthChoiceCounts?: {
    diningIn?: number;
    takeaway?: number;
    online?: number;
  };
  monthlyRevenueLast6Months?: Array<{
    month?: string;
    revenue?: number;
  }>;
  revenueData?: {
    today?: Array<{ label: string; revenue: number }>;
    thisWeek?: Array<{ label: string; revenue: number }>;
    thisMonth?: Array<{ label: string; revenue: number }>;
  };
  trendingItem?: {
    id?: string;
    name?: string;
    imageUrl?: string;
  };
  trendingItems?: {
    daily?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
    weekly?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
    monthly?: {
      id?: string;
      name?: string;
      imageUrl?: string;
    } | null;
  };
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token for admin routes
api.interceptors.request.use(
  (config) => {
    // Add auth token for admin routes and protected routes (menu, orders, loyalty)
    const url = config.url || "";
    const path = window.location.pathname || "";
    const isAdminRoute =
      url.includes("/admin") ||
      url.includes("/menu") ||
      url.includes("/orders") ||
      url.includes("/loyalty") ||
      url.includes("/discounts") ||
      url.includes("/newreg") ||
      url.includes("/codes") ||
      url.includes("/categories") ||
      url.includes("/tax") ||
      url.includes("/manage") ||
      url.includes("/roles") ||
      url.includes("/new") ||
      url.includes("/loyalty") ||
      url.includes("/upload-image") ||
      url.includes("/discount") ||
      url.includes("/platform") ||
      url.includes("/promotional-emails") ||
      url.includes("/restaurant-menu") ||
      url.includes("/reports") ||
      url.includes("/transactions");

    const isKitchenApp = path.startsWith("/kitchen");
    const isSalesApp = path.startsWith("/Sales");

    if (isKitchenApp || isSalesApp) {
      const token = Cookies.get("emptoken");
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    if (isAdminRoute) {
      const token = Cookies.get("token");

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // Remove Authorization header for non-admin routes
      if (config.headers && "Authorization" in config.headers) {
        delete config.headers.Authorization;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling auth errors for admin routes
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 for admin and protected routes, but NOT for login route
    const url = error.config?.url || "";
    const isAdminRoute =
      url.includes("/admin") ||
      url.includes("/menu") ||
      url.includes("/orders") ||
      url.includes("/loyalty") ||
      url.includes("/wallet") ||
      url.includes("/reports");
    const isLoginRoute = url.includes("/admin/login");

    const redirectToLogin = () => {
      const path = window.location.pathname || "";
      if (path.startsWith("/kitchen")) return "/kitchen/login";
      if (path.startsWith("/Sales")) return "/login";
      if (
        path === "/" ||
        path.startsWith("/pos") ||
        path.startsWith("/choice") ||
        path.startsWith("/pay")
      ) {
        return "/";
      }
      return "/admin/login";
    };

    // Only redirect on 401 for admin routes, but NOT for login route
    if (error.response?.status === 401 && isAdminRoute && !isLoginRoute) {
      Cookies.remove("token");
      window.location.href = redirectToLogin();
    }
    return Promise.reject(error);
  },
);

export type { ApiResponse };
export default api;

// Category CRUD API
export const getCategories = () => api.get("/categories");
export const getPresets = async (categoryId: string) =>
  api.get(`/presets/category/${categoryId}`);
export const getCodes = () => api.get("/codes");
export const createCodes = (data: any) => api.post("/codes/create", data);
export const updateCodes = (id: string, data: any) =>
  api.put(`/codes/${id}`, data);
export const deleteCodes = (id: string) => api.delete(`/codes/${id}`);
export const createCategory = (data: { name: string }) =>
  api.post("/categories", data);
export const updateCategory = (id: string, data: { name: string }) =>
  api.put(`/categories/${id}`, data);
export const deleteCategory = (id: string) => api.delete(`/categories/${id}`);
export const checkoutSession = (data: {
  amount: number;
  productName: string;
}) => {
  return api.post("/stripe/create-checkout-session", data);
};

export const paymentSession = (session_id: string) => {
  return api.get(`/stripe/get-payment-session?session_id=${session_id}`);
};

export const saveTransactions = (data: {
  phoneNumber: string;
  amount: string;
  currency: string;
  transaction_id: string;
}) => {
  return api.post("/stripe/save-transactions", data);
};

// Square Payment API
export const createSquarePayment = (data: {
  token: string;
  amount: number;
  orderId?: string;
}) => {
  return api.post("/square/payment", data);
};

export const getTax = (country: string) => api.get(`/tax/${country}`);
export const getTaxGrouped = (country: string = "US") => api.get(`/tax/${country}/grouped`);
export const createTax = (country: string, data: any) => api.post(`/tax/${country}`, data);
export const updateTax = (country: string, id: string, data: any) => api.put(`/tax/${country}/${id}`, data);
export const deleteTax = (country: string, id: string) => api.delete(`/tax/${country}/${id}`);
export const getPlatformLogo = () => api.get(`/platform/logo`);

//Roles
export const getRoles = () => api.get("/roles");
export const createRole = (data: { name: string }) => api.post("/roles", data);
export const deleteRole = (id: string) => api.delete(`/roles/${id}`);

//OrderStatus
export const updateOrderStatus = async (id: string, orderStatus: string) =>
  api.patch(`/orders/${id}/status`, { orderStatus });

export const updateOrderDelivery = async (
  id: string,
  deliveryProvider: "DoorDash" | "Uber" | "Custom Delivery",
) => api.patch(`/orders/${id}/delivery`, { deliveryProvider });

export const getDeliveryQuote = async (data: {
  dropoff_address: string;
  dropoff_latitude: number;
  dropoff_longitude: number;
  pickup_time: string;
}) => api.post("/orders/delivery/quote", data);

export const updateOrderDeliveryExternal = async (data: {
  orderId: string;
  platform: string;
  deliveryId?: string;
  tipAmount?: number;
  metadata?: Record<string, any>;
  items?: any[];
  totalAmount?: number;
  userId?: string;
  orderBy?: string;
}) => api.post("/order/update-delivery", data);

export const cancelOrderItem = (orderId: string, itemId: string) => {
  return api.put(`/orders/${orderId}/item/${itemId}/cancel`);
};

export const updateOrderScheduledAt = (orderId: string, scheduledAt: string) =>
  api.patch(`/orders/${orderId}/scheduled-at`, { scheduledAt });

// Preset APIs (using existing getPresets for category-based fetching)
export const getAllPresetsAPI = () => api.get("/presets");
export const createPresetAPI = (data: any) => api.post("/presets", data);
export const updatePresetAPI = (id: string, data: any) =>
  api.put(`/presets/${id}`, data);
export const deletePresetAPI = (id: string) => api.delete(`/presets/${id}`);

// Hotel CRUD API
export const getHotels = () => api.get("/admin/getHotelInformation");
export const updateHotel = (id: string, data: any) =>
  api.put(`/admin/hotels/${id}`, data);
export const deleteHotel = (id: string) => api.delete(`/admin/hotels/${id}`);

// Platform API
export const getPlatform = () => api.get("/platform");

export const updatePlatformLogo = (logo: string) =>
  api.put("/platform/logo", { logo });

export const updateApplePassLogo = (logo: string) =>
  api.put("/platform/apple-pass-logo", { logo });

export const deletePlatformLogo = () => api.delete("/platform/logo");
export const deleteApplePassLogo = () =>
  api.delete("/platform/apple-pass-logo");

export const updatePlatformCurrency = (currency: string) =>
  api.put("/platform/currency", { currency });

export const getPlatformIntegrations = () => api.get("/platform/integrations");
export const updatePlatformIntegration = (
  provider:
    | "baseEnv"
    | "mail"
    | "applePasskit"
    | "digitalOceanSpaces"
    | "square"
    | "stripe",
  data: { accountEmail?: string; credentials: Record<string, string> },
) => api.put(`/platform/integrations/${provider}`, data);

// Per-hotel (location) integrations for Uber Direct, DoorDash, Google Places
export const getLocationIntegrations = (hotelId: string) =>
  api.get(`/admin/hotels/${hotelId}/integrations`);
export const updateLocationIntegration = (
  hotelId: string,
  provider: "uberDirect" | "doordashDeveloper" | "googlePlaces" | "square" | "stripe",
  data: { accountEmail?: string; credentials: Record<string, string> },
) => api.put(`/admin/hotels/${hotelId}/integrations/${provider}`, data);

// Places API
export const getPlaceAutocomplete = (
  input: string,
  lat?: number,
  lng?: number,
  sessionToken?: string,
) =>
  api.get(
    `/admin/place-autocomplete?input=${encodeURIComponent(input)}${
      typeof lat === "number" && typeof lng === "number"
        ? `&lat=${lat}&lng=${lng}`
        : ""
    }${sessionToken ? `&sessionToken=${encodeURIComponent(sessionToken)}` : ""}`,
  );
export const getPlaceDetails = (lat: number, lng: number) =>
  api.get(`/admin/place-details?lat=${lat}&lng=${lng}`);
export const getPlaceDetailsById = (placeId: string, sessionToken?: string) =>
  api.get(
    `/admin/place-details-by-id?placeId=${encodeURIComponent(placeId)}${
      sessionToken ? `&sessionToken=${encodeURIComponent(sessionToken)}` : ""
    }`,
  );
export const getPlaceOpeningHours = (lat: number, lng: number) =>
  api.get(`/admin/place-opening-hours?lat=${lat}&lng=${lng}`);

// Restaurant Menu API
export const getRestaurantMenuItems = (hotelId: string) =>
  api.get(`/restaurant-menu/${hotelId}`);
export const updateRestaurantMenuItem = (
  hotelId: string,
  menuId: string,
  data: { price?: number | null; availability?: boolean; fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[] | null },
) => api.put(`/restaurant-menu/${hotelId}/${menuId}`, data);
export const bulkUpdateRestaurantMenuItems = (
  hotelId: string,
  items: Array<{
    menuId: string;
    price?: number | null;
    availability?: boolean;
    fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[] | null;
  }>,
) => api.post(`/restaurant-menu/${hotelId}/bulk`, { items });
export const deleteRestaurantMenuItem = (hotelId: string, menuId: string) =>
  api.delete(`/restaurant-menu/${hotelId}/${menuId}`);
export const getRestaurantMenuByMenuId = (menuId: string) =>
  api.get(`/restaurant-menu/by-menu/${menuId}`);

// Reward Campaigns API (wallet endpoints)
export const getRewardCampaigns = () => api.get("/admin/getUserGiftCards");
export const createRewardCampaign = (data: {
  amount: number;
  expiresAt: string;
}) => api.post("/admin/createUserGiftCard", data);
export const updateRewardCampaign = (
  id: string,
  data: { amount: number; expiresAt: string },
) => api.put(`/admin/updateUserGiftCard/${id}`, data);
export const deleteRewardCampaign = (id: string) =>
  api.delete(`/admin/deleteUserGiftCard/${id}`);

// Reports API
export const getSalesReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/sales", { params });

export const getPaymentsReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/payments", { params });

export const getItemsReport = (params: {
  startDate?: string;
  endDate?: string;
  orderSource?: string;
  orderType?: string;
  restaurantId?: string;
}) => api.get("/reports/items", { params });

export const cancelOrder = (orderId: string) => api.post(`/orders/${orderId}/cancel`);

export const processOrderRefunds = (orderId: string) =>
  api.post(`/transactions/order/${orderId}/process-refunds`);

export const verifyEmployeePasscode = (restaurantId: string, passcode: string) =>
  api.post("/new/verify-passcode", { restaurantId, passcode });

// Location Admin Management API
export const getLocationAdmins = () => api.get("/admin/location-admins");
export const createLocationAdmin = (data: {
  email: string;
  password: string;
  restaurantId: string;
}) => api.post("/admin/location-admins", data);
export const updateLocationAdmin = (
  id: string,
  data: { email?: string; password?: string; restaurantId?: string },
) => api.put(`/admin/location-admins/${id}`, data);
export const deleteLocationAdmin = (id: string) =>
  api.delete(`/admin/location-admins/${id}`);
